import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { HorizontalPickerQuestion } from '../profile-question.model';

/** Pixels of horizontal drag that move the wheel by one value. */
const DRAG_STEP = 44;

/** Figma: IVY-HA 235:16358 (desktop) / 235:16283 (mobile). */
@Component({
  selector: 'app-horizontal-picker-question',
  standalone: true,
  imports: [NgClass, NgFor, NgIf],
  templateUrl: './horizontal-picker-question.component.html'
})
export class HorizontalPickerQuestionComponent implements OnInit {
  @Input({ required: true }) question!: HorizontalPickerQuestion;
  /** The picked number, or the question's default for NOT SURE. */
  @Output() answer = new EventEmitter<number>();

  value = 0;
  /** Neighbours drawn either side of the selected value, with their x offset from the centre. */
  readonly slots = [
    { offset: -2, x: -138 },
    { offset: -1, x: -88 },
    { offset: 1, x: 88 },
    { offset: 2, x: 138 },
  ];
  private dragStartX: number | null = null;
  private dragStartValue = 0;
  /** Set once a drag moves the wheel, so the click that ends it doesn't also select a neighbour. */
  private dragged = false;

  ngOnInit() {
    this.value = this.clamp(this.question.initial);
  }

  neighbour(offset: number): number | null {
    const candidate = this.value + offset;
    return candidate >= this.question.min && candidate <= this.question.max ? candidate : null;
  }

  select(value: number | null) {
    if (this.dragged) {
      this.dragged = false;
      return;
    }
    if (value !== null) {
      this.value = this.clamp(value);
    }
  }

  step(by: number) {
    this.value = this.clamp(this.value + by);
  }

  onWheel(event: WheelEvent) {
    event.preventDefault();
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    this.step(Math.sign(delta));
  }

  onPointerDown(event: PointerEvent) {
    this.dragStartX = event.clientX;
    this.dragStartValue = this.value;
    this.dragged = false;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent) {
    if (this.dragStartX === null) return;
    // Dragging left reveals larger values, like scrolling a ruler
    const steps = Math.round((this.dragStartX - event.clientX) / DRAG_STEP);
    if (steps !== 0) {
      this.dragged = true;
    }
    this.value = this.clamp(this.dragStartValue + steps);
  }

  onPointerUp() {
    this.dragStartX = null;
  }

  private clamp(value: number): number {
    return Math.min(this.question.max, Math.max(this.question.min, Math.round(value)));
  }
}
